import React, { useState, useEffect, useCallback } from 'react';
import { FaUsers, FaCheckCircle, FaIdCard, FaWallet, FaEnvelope } from 'react-icons/fa';
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import api from '@/utils/api';
import './UserGrowth.scss';

type Granularity = 'day' | 'week' | 'month';

interface SeriesPoint {
  period: string;
  totalSignups: number;
  emailSignups: number;
  walletSignups: number;
  verifications: number;
  profilesCreated: number;
}

interface Funnel {
  totalSignups: number;
  emailUsers: number;
  walletUsers: number;
  emailVerified: number;
  profilesCreated: number;
  verificationRate: number;
  profileCreationRate: number;
}

interface GrowthData {
  series: SeriesPoint[];
  funnel: Funnel;
}

const PRESETS: { label: string; days: number }[] = [
  { label: '7D',  days: 7   },
  { label: '30D', days: 30  },
  { label: '90D', days: 90  },
  { label: '1Y',  days: 365 },
];

function toISODate(d: Date) {
  return d.toISOString().split('T')[0];
}

function daysAgo(n: number) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return toISODate(d);
}

function formatPeriodLabel(period: string, gran: Granularity) {
  const d = new Date(period + 'T00:00:00');
  if (gran === 'day')   return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  if (gran === 'week')  return `Wk ${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
  return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
}

const UserGrowthPage: React.FC = () => {
  const today = toISODate(new Date());

  const [granularity, setGranularity]   = useState<Granularity>('day');
  const [from, setFrom]                 = useState(daysAgo(30));
  const [to, setTo]                     = useState(today);
  const [activePreset, setActivePreset] = useState<number>(30);
  const [data, setData]                 = useState<GrowthData | null>(null);
  const [loading, setLoading]           = useState(false);
  const [error, setError]               = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get<GrowthData>('/admin/user-growth', {
        params: { granularity, from, to },
      });
      setData(res.data);
    } catch (err: any) {
      setError(err?.response?.data?.error ?? 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [granularity, from, to]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const applyPreset = (days: number) => {
    setActivePreset(days);
    setFrom(daysAgo(days));
    setTo(today);
    // Auto-switch granularity to something sensible
    if (days <= 14)  setGranularity('day');
    else if (days <= 90) setGranularity('day');
    else setGranularity('month');
  };

  const chartData = data?.series.map(p => ({
    ...p,
    label: formatPeriodLabel(p.period, granularity),
  })) ?? [];

  const funnel = data?.funnel;

  return (
    <div className="ug-page">
      <div className="ug-header">
        <h1>User Growth</h1>
        <p>Sign-up funnel broken down by auth method, verification, and profile creation.</p>
      </div>

      {/* ── Controls ─────────────────────────────────────────────────────── */}
      <div className="ug-controls">
        <div className="ug-presets">
          {PRESETS.map(p => (
            <button
              key={p.days}
              className={`ug-preset-btn ${activePreset === p.days ? 'active' : ''}`}
              onClick={() => applyPreset(p.days)}
            >
              {p.label}
            </button>
          ))}
        </div>

        <div className="ug-date-range">
          <label>From
            <input
              type="date"
              value={from}
              max={to}
              onChange={e => { setFrom(e.target.value); setActivePreset(0); }}
            />
          </label>
          <span className="ug-date-sep">→</span>
          <label>To
            <input
              type="date"
              value={to}
              min={from}
              max={today}
              onChange={e => { setTo(e.target.value); setActivePreset(0); }}
            />
          </label>
        </div>

        <div className="ug-granularity">
          {(['day', 'week', 'month'] as Granularity[]).map(g => (
            <button
              key={g}
              className={`ug-gran-btn ${granularity === g ? 'active' : ''}`}
              onClick={() => setGranularity(g)}
            >
              {g.charAt(0).toUpperCase() + g.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {error && <div className="ug-error">{error}</div>}

      {/* ── Funnel cards ─────────────────────────────────────────────────── */}
      <div className="ug-funnel">
        <div className="ug-funnel-card">
          <FaUsers className="ug-funnel-icon" />
          <div className="ug-funnel-body">
            <span className="ug-funnel-value">
              {loading ? '—' : (funnel?.totalSignups ?? 0).toLocaleString()}
            </span>
            <span className="ug-funnel-label">Total Sign-ups</span>
          </div>
          <div className="ug-funnel-split">
            <span><FaEnvelope /> {loading ? '—' : (funnel?.emailUsers ?? 0).toLocaleString()} email</span>
            <span><FaWallet />   {loading ? '—' : (funnel?.walletUsers ?? 0).toLocaleString()} wallet</span>
          </div>
        </div>

        <div className="ug-funnel-arrow">▶</div>

        <div className="ug-funnel-card">
          <FaCheckCircle className="ug-funnel-icon verified" />
          <div className="ug-funnel-body">
            <span className="ug-funnel-value">
              {loading ? '—' : (funnel?.emailVerified ?? 0).toLocaleString()}
            </span>
            <span className="ug-funnel-label">Email Verified</span>
          </div>
          <div className="ug-funnel-rate">
            {loading ? '—' : `${funnel?.verificationRate ?? 0}%`} of email users
          </div>
        </div>

        <div className="ug-funnel-arrow">▶</div>

        <div className="ug-funnel-card">
          <FaIdCard className="ug-funnel-icon profile" />
          <div className="ug-funnel-body">
            <span className="ug-funnel-value">
              {loading ? '—' : (funnel?.profilesCreated ?? 0).toLocaleString()}
            </span>
            <span className="ug-funnel-label">Profiles Created</span>
          </div>
          <div className="ug-funnel-rate">
            {loading ? '—' : `${funnel?.profileCreationRate ?? 0}%`} of all sign-ups
          </div>
        </div>
      </div>

      {/* ── Chart ────────────────────────────────────────────────────────── */}
      <div className="ug-chart-card">
        <h2>Growth Over Time</h2>
        {loading ? (
          <div className="ug-loading">Loading…</div>
        ) : chartData.length === 0 ? (
          <div className="ug-empty">No data for this period.</div>
        ) : (
          <ResponsiveContainer width="100%" height={340}>
            <ComposedChart data={chartData} margin={{ top: 8, right: 24, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" />
              <XAxis
                dataKey="label"
                tick={{ fill: 'var(--text-secondary)', fontSize: 12 }}
                tickLine={false}
              />
              <YAxis
                tick={{ fill: 'var(--text-secondary)', fontSize: 12 }}
                tickLine={false}
                axisLine={false}
                allowDecimals={false}
              />
              <Tooltip
                contentStyle={{
                  background: 'var(--card-background)',
                  border: '1px solid var(--border-color)',
                  borderRadius: '8px',
                  color: 'var(--text-primary)',
                }}
              />
              <Legend wrapperStyle={{ color: 'var(--text-secondary)', fontSize: 13 }} />

              {/* Stacked bars: email + wallet sign-ups */}
              <Bar dataKey="emailSignups"  name="Email sign-ups"  stackId="signups" fill="var(--primary-color)"  radius={[0,0,0,0]} />
              <Bar dataKey="walletSignups" name="Wallet sign-ups" stackId="signups" fill="#8b5cf6" radius={[4,4,0,0]} />

              {/* Lines: verifications and profiles */}
              <Line dataKey="verifications"   name="Email verified"      type="monotone" stroke="#22c55e" strokeWidth={2} dot={false} />
              <Line dataKey="profilesCreated" name="Profiles created"    type="monotone" stroke="#f59e0b" strokeWidth={2} dot={false} />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* ── Data table ───────────────────────────────────────────────────── */}
      {!loading && chartData.length > 0 && (
        <div className="ug-table-card">
          <h2>Period Breakdown</h2>
          <div className="ug-table-wrap">
            <table className="ug-table">
              <thead>
                <tr>
                  <th>Period</th>
                  <th>Email</th>
                  <th>Wallet</th>
                  <th>Total</th>
                  <th>Verified</th>
                  <th>Profiles</th>
                </tr>
              </thead>
              <tbody>
                {chartData.map(row => (
                  <tr key={row.period}>
                    <td>{row.label}</td>
                    <td>{row.emailSignups.toLocaleString()}</td>
                    <td>{row.walletSignups.toLocaleString()}</td>
                    <td><strong>{row.totalSignups.toLocaleString()}</strong></td>
                    <td>{row.verifications.toLocaleString()}</td>
                    <td>{row.profilesCreated.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserGrowthPage;
